import React, { useState, useEffect, useCallback } from "react";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Grid,
  Card,
  Box,
  Autocomplete,
  IconButton,
} from "@mui/material";
import axios from "../../api";
import { AxiosError } from "axios";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useDropzone } from "react-dropzone";
import AWS from "aws-sdk";
import StatusHistory from "examples/ReuseFunctions/StatusHistory";
import { useSelector } from "react-redux";
import DeleteIcon from "@mui/icons-material/Delete";
import StatusTracker from "examples/ReuseFunctions/StatusTracker";
import CircularProgress from "@mui/material/CircularProgress";
import DateFormat from "examples/ReuseFunctions/DateFormat";

interface AssetType {
  asset_type_id: string;
  asset_type_name: string;
}

interface Category {
  category_id: string;
  name: string;
}

interface Asset {
  asset_id: string;
  asset_name: string;
  address_id: string;
  address_type: string;
  city_name: string;
  region_name: string;
  state_name?: string;
  address?: string;
  asset_type_id?: string;
  asset_type_name?: string;
  division_id?: number;
  division_name?: string;
  asset_sku?: string;
}

interface BranchHeadDivision {
  division_id: number;
  division_name: string;
  customer_id: string;
}

interface FormValues {
  assigned_employee_id: string | null;
  priority_rank: string | null;
  description: string;
  status_id: string;
  address_id: string;
  file_name: string;
  file_type: string;
  asset_type_id: string;
  asset_id: string;
  title: string;
  urgency: string | number;
  category_id?: string;
  division_id: string;
  serial_number: string;
}

interface User {
  name: string;
  userId: string;
}

interface RootState {
  auth: {
    user: User;
  };
}

interface UploadedFile extends File {
  preview: string;
}

interface MediaData {
  file_name: string;
  file_type: string;
  file_path: string;
}

interface TicketData {
  category_id?: string;
  description: string;
  address_id: string;
  customer_id: string;
  asset_type_id: string;
  asset_id: string;
  division_id: string | null;
  priority_rank: string | null;
  status_id: number;
  assigned_employee_id: string | null;
  title: string;
  urgency: number | null;
  assignee_history?: string;
  status_history?: any;
  status_tracker?: any;
}

const validationSchema = Yup.object({
  division_id: Yup.string().required("Division is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  asset_id: Yup.string().required("Asset is required"),
  description: Yup.string().required("Description is required"),
  address_id: Yup.string().required("Address is required"),
  title: Yup.string().required("Title is required"),
  urgency: Yup.number(),
});

const initialValues: FormValues = {
  assigned_employee_id: null,
  priority_rank: null,
  description: "",
  status_id: "",
  address_id: "",
  file_name: "",
  file_type: "",
  asset_type_id: "",
  asset_id: "",
  division_id: "",
  title: "",
  urgency: "",
  serial_number: "",
};

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

export interface BranchHeadAddTicketProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const normalizeSerialAsset = (asset: any, divisionId: number): Asset => ({
  asset_id: String(asset.asset_id),
  asset_name: asset.asset_name,
  address_id: asset.address_id ? String(asset.address_id) : "",
  address_type: asset.address_type ?? "",
  city_name: asset.city_name ?? "",
  region_name: asset.region_name ?? "",
  state_name: asset.state_name ?? "",
  address: asset.address ?? "",
  asset_type_id: asset.asset_type_id ? String(asset.asset_type_id) : undefined,
  asset_type_name: asset.asset_type_name,
  division_id: divisionId,
  division_name: asset.division_name ?? "",
  asset_sku: asset.asset_sku,
});

// --- Extracted: resolve asset-type option from existing list or raw asset fields ---
const resolveAssetTypeOption = (
  normalizedAsset: Asset,
  assetsData: AssetType[] | null
): AssetType | null =>
  assetsData?.find(
    (option) =>
      String(option.asset_type_id) === String(normalizedAsset.asset_type_id)
  ) ??
  (normalizedAsset.asset_type_id && normalizedAsset.asset_type_name
    ? {
        asset_type_id: normalizedAsset.asset_type_id,
        asset_type_name: normalizedAsset.asset_type_name ?? "",
      }
    : null);

// --- Extracted: upsert an asset into a list by asset_id ---
const upsertAssetList = (list: Asset[], incoming: Asset): Asset[] => {
  const index = list.findIndex(
    (item) => String(item.asset_id) === String(incoming.asset_id)
  );
  if (index === -1) return [...list, incoming];
  const clone = [...list];
  clone[index] = { ...clone[index], ...incoming };
  return clone;
};

const BranchHeadAddTicket: React.FC<BranchHeadAddTicketProps> = ({
  embedded,
  onSuccess,
  onCancel,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [categories, setCategories] = useState<Category[]>([]);
  const [divisions, setDivisions] = useState<BranchHeadDivision[]>([]);
  const [selectedDivision, setSelectedDivision] = useState<BranchHeadDivision | null>(null);
  const [selectedAssetTypeId, setSelectedAssetTypeId] = useState<string | null>(null);
  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<Asset | null>(null);
  const [serialLookupLoading, setSerialLookupLoading] = useState(false);
  const [serialLookupError, setSerialLookupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const formikApiRef = React.useRef<{
    setFieldValue: (field: string, value: unknown) => void;
  } | null>(null);

  useEffect(() => {
    const fetchDivisions = async () => {
      if (!user?.userId) return;
      try {
        const response = await axios.get(
          `/employee/branch-head/divisions/${user.userId}`
        );
        setDivisions(response?.data?.divisions ?? []);
      } catch (err) {
        console.error("Error fetching branch head divisions", err);
        fetchError(
          (err as any)?.response?.data?.error ||
            "Failed to load divisions. You may have no divisions assigned."
        );
      }
    };
    fetchDivisions();
  }, [user?.userId, fetchError]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesResponse = await axios.get(
          `/category/asset-type/${selectedAssetTypeId}`,
          { params: { is_active: 1 } }
        );
        setCategories(categoriesResponse.data.list ?? []);
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    if (selectedAssetTypeId) fetchData();
    else setCategories([]);
  }, [selectedAssetTypeId]);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles: File[]) => {
      setUploadedFiles((prev) => [
        ...prev,
        ...acceptedFiles.map((file) =>
          Object.assign(file, { preview: URL.createObjectURL(file) })
        ),
      ]);
    },
  });

  const handleDelete = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const [assetsData, setAssetsData] = useState<AssetType[] | null>(null);

  const applyAssetTypeFromSerial = useCallback(
    (
      normalizedAsset: Asset,
      setFieldValue: (field: string, value: unknown) => void
    ) => {
      const assetTypeOption = resolveAssetTypeOption(normalizedAsset, assetsData);
      if (assetTypeOption) {
        setSelectedAssetType(assetTypeOption);
        setSelectedAssetTypeId(assetTypeOption.asset_type_id);
        setFieldValue("asset_type_id", assetTypeOption.asset_type_id);
      } else if (normalizedAsset.asset_type_id) {
        setSelectedAssetTypeId(normalizedAsset.asset_type_id);
        setFieldValue("asset_type_id", normalizedAsset.asset_type_id);
      }
    },
    [assetsData]
  );

  const applyAssetFromSerial = useCallback(
    (
      normalizedAsset: Asset,
      setFieldValue: (field: string, value: unknown) => void,
      rawAsset: any,
      trimmed: string
    ) => {
      setDivisionAssets((prev) => upsertAssetList(prev, normalizedAsset));
      setSelectedAsset(normalizedAsset);
      setFieldValue("asset_id", normalizedAsset.asset_id);
      setSelectedAddress(normalizedAsset);
      setFieldValue("address_id", normalizedAsset.address_id || "");
      setFieldValue("serial_number", rawAsset.asset_sku || trimmed);
    },
    []
  );

  const handleSerialLookup = async (
    serialInput: string,
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const trimmed = serialInput?.trim();
    if (!trimmed) {
      setSerialLookupError("Enter a serial number to search");
      return;
    }
    setSerialLookupLoading(true);
    setSerialLookupError(null);
    try {
      const response = await axios.get(`/assets/serial/${encodeURIComponent(trimmed)}`);
      const asset = response?.data?.asset;
      if (!asset) throw new Error("Asset not found");
      if (!asset.division_id) throw new Error("Asset is missing division details.");

      const divisionId = Number(asset.division_id);
      const divisionOption = divisions.find((div) => div.division_id === divisionId) ?? null;
      if (!divisionOption) throw new Error("This asset belongs to a division you cannot access.");

      setSelectedDivision(divisionOption);
      setFieldValue("division_id", String(divisionOption.division_id));

      const normalizedAsset = normalizeSerialAsset(asset, divisionId);

      applyAssetTypeFromSerial(normalizedAsset, setFieldValue);

      setSelectedCategory(null);
      setFieldValue("category_id", "");

      applyAssetFromSerial(normalizedAsset, setFieldValue, asset, trimmed);

      fetchSuccess("Asset details auto-filled from serial number.");
    } catch (error) {
      const message =
        error instanceof AxiosError && error.response?.data?.error
          ? error.response.data.error
          : (error as Error).message || "Asset not found for that serial number";
      setSerialLookupError(message);
      fetchError(message);
    } finally {
      setSerialLookupLoading(false);
    }
  };

  const fetchAssetTypes = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", { params: { is_active: 1 } });
      const list =
        response?.data?.list && Array.isArray(response.data.list)
          ? response.data.list
          : [];
      setAssetsData(list);
    } catch (err) {
      console.error("Error fetching asset types", err);
    }
  }, []);

  useEffect(() => {
    fetchAssetTypes();
  }, [fetchAssetTypes]);

  const [divisionAssets, setDivisionAssets] = useState<Asset[]>([]);

  const fetchAssetsByDivisionAndAssetType = useCallback(async () => {
    if (
      !selectedDivision?.division_id ||
      !selectedAssetType?.asset_type_id ||
      !selectedDivision?.customer_id
    )
      return;
    try {
      const response = await axios.get(
        `/assets/${selectedDivision.division_id}/${selectedAssetType.asset_type_id}/${selectedDivision.customer_id}`
      );
      setDivisionAssets(response?.data?.list ?? []);
    } catch (err) {
      console.error("Error fetching assets", err);
      setDivisionAssets([]);
    }
  }, [
    selectedDivision?.division_id,
    selectedDivision?.customer_id,
    selectedAssetType?.asset_type_id,
  ]);

  useEffect(() => {
    if (
      selectedDivision?.division_id &&
      selectedAssetType?.asset_type_id &&
      selectedDivision?.customer_id
    ) {
      fetchAssetsByDivisionAndAssetType();
    } else {
      setDivisionAssets([]);
    }
  }, [fetchAssetsByDivisionAndAssetType]);

  useEffect(() => {
    if (
      !selectedDivision?.division_id ||
      !assetsData?.length ||
      assetsData.length !== 1
    )
      return;
    const at = assetsData[0];
    const setFieldValue = formikApiRef.current?.setFieldValue;
    if (setFieldValue) {
      setFieldValue("asset_type_id", at.asset_type_id);
    }
    setSelectedAssetType(at);
    setSelectedAssetTypeId(at.asset_type_id);
  }, [selectedDivision?.division_id, assetsData]);

  useEffect(() => {
    if (
      !divisionAssets?.length ||
      divisionAssets.length !== 1 ||
      !selectedAssetType?.asset_type_id
    )
      return;
    const asset = divisionAssets[0];
    const setFieldValue = formikApiRef.current?.setFieldValue;
    if (setFieldValue) {
      setFieldValue("asset_id", asset.asset_id);
      setFieldValue("address_id", asset.address_id ?? "");
    }
    setSelectedAsset(asset);
    setSelectedAddress(asset);
  }, [divisionAssets, selectedAssetType?.asset_type_id]);

  const currentTime = DateFormat();

  const content = (
    <Card
      style={{
        padding: embedded ? "16px" : "24px",
        width: "100%",
        borderRadius: "8px",
      }}
    >
      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Ticket
        </SoftTypography>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (
          values: FormValues,
          { setSubmitting: formikSetSubmitting }: FormikHelpers<FormValues>
        ) => {
          if (!selectedDivision?.customer_id) {
            fetchError("Please select a division.");
            return;
          }
          setSubmitting(true);
          setLoading(true);
          const ticketData: TicketData = {
            category_id: values?.category_id,
            description: values?.description,
            address_id: values?.address_id,
            customer_id: selectedDivision.customer_id,
            asset_type_id: values?.asset_type_id,
            asset_id: values?.asset_id,
            division_id: values?.division_id || null,
            priority_rank: null,
            status_id: 1,
            assigned_employee_id: null,
            title: values?.title,
            urgency: null,
          };

          ticketData.status_history = StatusHistory(null, user?.name, "Open");
          ticketData.status_tracker = StatusTracker({
            previousData: null,
            message: "Ticket is Created",
            statusName: "Open",
            status_id: 1,
            createdby: user?.name,
            employeeName: "",
            employeePhone: "",
            arraivalDate: currentTime,
          });

          let mediaDataArray: MediaData[] = [];
          try {
            if (uploadedFiles?.length > 0) {
              const uploadFileToS3 = async (file: UploadedFile): Promise<MediaData> => {
                const fileName = `${Date.now()}-${file.name}`;
                await s3
                  .upload({
                    Bucket: process.env.REACT_APP_AWS_BUCKET_NAME!,
                    Key: fileName,
                    Body: file,
                    ContentType: file.type,
                    ACL: "public-read",
                  })
                  .promise();
                const type = file.type.startsWith("image") ? "Photo" : file.type.split("/")[0];
                return { file_name: fileName, file_type: type, file_path: fileName };
              };
              mediaDataArray = await Promise.all(uploadedFiles.map(uploadFileToS3));
            }

            const payload =
              mediaDataArray.length > 0
                ? { ticketData, mediaData: mediaDataArray }
                : { ticketData };

            const response = await axios.post("/tickets", payload);
            fetchSuccess(response.data.message);
            if (embedded && onSuccess) {
              onSuccess();
            } else {
              navigate("/branch-head/tickets-list");
            }
          } catch (error: any) {
            fetchError(error?.response?.data?.error || "Something went wrong");
          } finally {
            setSubmitting(false);
            setLoading(false);
          }
        }}
      >
        {({ errors, touched, setFieldValue, values }) => {
          formikApiRef.current = { setFieldValue };
          return (
            <Form
              className="new-ticket-form"
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Title
                    </SoftTypography>
                    <Field
                      name="title"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.title && touched.title}
                      helperText={touched.title && errors.title}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Serial Number (optional)
                    </SoftTypography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        name="serial_number"
                        value={values.serial_number}
                        onChange={(event) => {
                          setFieldValue("serial_number", event.target.value);
                          if (serialLookupError) {
                            setSerialLookupError(null);
                          }
                        }}
                        placeholder="Enter asset serial / SKU"
                        variant="outlined"
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        color="primary"
                        disabled={serialLookupLoading}
                        onClick={() =>
                          handleSerialLookup(values.serial_number, setFieldValue)
                        }
                      >
                        {serialLookupLoading ? "Searching..." : "Fetch"}
                      </Button>
                    </Box>
                    {serialLookupError && (
                      <SoftTypography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {serialLookupError}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Division
                    </SoftTypography>
                    <Autocomplete
                      options={divisions}
                      getOptionLabel={(option) => option.division_name}
                      value={selectedDivision}
                      onChange={(event, value) => {
                        setFieldValue("division_id", value ? String(value.division_id) : "");
                        setSelectedDivision(value);
                        setFieldValue("asset_type_id", "");
                        setFieldValue("asset_id", "");
                        setFieldValue("address_id", "");
                        setFieldValue("category_id", "");
                        setSelectedAssetType(null);
                        setSelectedAssetTypeId(null);
                        setSelectedAsset(null);
                        setSelectedAddress(null);
                        setSelectedCategory(null);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.division_id && touched.division_id}
                          helperText={touched.division_id && errors.division_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Asset Type
                    </SoftTypography>
                    <Autocomplete
                      options={assetsData || []}
                      getOptionLabel={(option) => `${option.asset_type_name}`}
                      value={selectedAssetType}
                      onChange={(event, value) => {
                        setFieldValue("asset_type_id", value?.asset_type_id || "");
                        setFieldValue("category_id", "");
                        setSelectedAssetType(value);
                        setSelectedAssetTypeId(value?.asset_type_id || null);
                        setSelectedCategory(null);
                        setSelectedAsset(null);
                        setSelectedAddress(null);
                        setFieldValue("asset_id", "");
                        setFieldValue("address_id", "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_type_id && !!touched.asset_type_id}
                          helperText={touched.asset_type_id && errors.asset_type_id}
                        />
                      )}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Assets
                    </SoftTypography>
                    <Autocomplete
                      options={divisionAssets}
                      getOptionLabel={(option) => option.asset_name || ""}
                      value={selectedAsset}
                      onChange={(event, value) => {
                        setSelectedAddress(value);
                        setSelectedAsset(value);
                        setFieldValue("asset_id", value ? value.asset_id : "");
                        setFieldValue("address_id", value ? value.address_id : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.asset_id && !!touched.asset_id}
                          helperText={touched.asset_id && errors.asset_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Category
                    </SoftTypography>
                    <Autocomplete
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={selectedCategory}
                      onChange={(event, value) => {
                        setFieldValue("category_id", value?.category_id || "");
                        setSelectedCategory(value);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          error={!!errors.category_id && touched.category_id}
                          helperText={touched.category_id && errors.category_id}
                        />
                      )}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Address
                    </SoftTypography>
                    <TextField
                      name="address_id"
                      fullWidth
                      value={
                        selectedAddress
                          ? `${selectedAddress?.address_type}-${selectedAddress?.city_name}-${selectedAddress?.region_name}`
                          : ""
                      }
                      variant="outlined"
                      disabled
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Description
                    </SoftTypography>
                    <Field
                      name="description"
                      as={TextField}
                      variant="outlined"
                      fullWidth
                      error={!!errors.description && touched.description}
                      helperText={touched.description && errors.description}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={12}>
                  {uploadedFiles.length > 0 && (
                    <div className="upload-media">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={file.name + "-" + file.lastModified}
                          className="media-container"
                          style={{ position: "relative" }}
                        >
                          {file.preview &&
                            (file.type.startsWith("image") ? (
                              <img
                                src={file.preview}
                                alt={`media-${index}`}
                                className="upload-img"
                              />
                            ) : (
                              <video
                                className="upload-video"
                                controls
                                width="100%"
                                height="140px"
                              >
                                <source src={file.preview} type="video/mp4" />
                                <track kind="captions" />
                              </video>
                            ))}
                          <IconButton onClick={() => handleDelete(index)} color="secondary">
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      ))}
                    </div>
                  )}
                  <Box
                    sx={{ cursor: "pointer", width: "fit-content" }}
                    {...dropzone.getRootProps()}
                  >
                    <Box>
                      <label htmlFor="image-upload-input">
                        <input {...dropzone.getInputProps({ name: "file_name" })} />
                        <Button
                          component="span"
                          variant="contained"
                          color="primary"
                          className="upload-btn"
                        >
                          Upload File
                        </Button>
                      </label>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button
                    className="success-btn"
                    sx={{ position: "relative", minWidth: 100 }}
                    variant="contained"
                    type="submit"
                    disabled={submitting || loading}
                    color="success"
                  >
                    {loading ? <CircularProgress size={20} /> : "Save"}
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{ position: "relative", minWidth: 100, ml: 2.5 }}
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      embedded && onCancel
                        ? onCancel()
                        : navigate("/branch-head/tickets-list")
                    }
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Card>
  );

  if (embedded) return content;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default BranchHeadAddTicket;